import { PATH } from '@/constants/path';

export const ASSETS = '/assets/celebrating-20-years';

export type VideoContent = {
  poster: string;
  duration: string;
  title: string;
  // The ID from the YouTube URL (youtube.com/watch?v=<id>)
  youtubeId?: string;
};

const MAIN_VIDEO_ID = 'T7pjFh_M8HI';

export const MAIN_VIDEO: VideoContent = {
  // The video's own YouTube thumbnail, so the poster always matches the video
  poster: `https://img.youtube.com/vi/${MAIN_VIDEO_ID}/maxresdefault.jpg`,
  duration: '2:43',
  title: "Why locuming made me a happier, healthier doctor: Ash's story",
  youtubeId: MAIN_VIDEO_ID,
};

export const STATS = [
  { value: '17,200+', label: 'Unique doctors placed' },
  { value: '20', label: 'Years running' },
  { value: '1,400+', label: 'Unique clients with placements' },
  { value: '2', label: 'Countries served, AU NZ' },
];

export type Testimonial = VideoContent & { name: string; topic: string };

export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Dr Alf Phillips',
    topic: 'on locuming with family',
    poster: `${ASSETS}/testimonial-alf-phillips.jpg`,
    duration: '1:42',
    title: 'Dr Alf Phillips on locuming with family',
    youtubeId: 'J9rwx3MH_7w',
  },
  {
    name: 'Dr Ash Bowden',
    topic: 'on Medrecruit',
    poster: `${ASSETS}/testimonial-ash-bowden.jpg`,
    duration: '1:27',
    title: 'Dr Ash Bowden on Medrecruit',
    youtubeId: 'Ye1TSJXOP7U',
  },
  {
    name: 'Dr Huw John',
    topic: 'on travelling as a locum',
    poster: `${ASSETS}/testimonial-huw-john.jpg`,
    duration: '0:48',
    title: 'Dr Huw John on travelling as a locum',
    youtubeId: 'H9-1M5OVRiI',
  },
];

export type MilestoneTile =
  | 'medrecruit'
  | 'photo'
  | 'photoFlipped'
  | 'deloitte'
  | 'medworld'
  | 'bcorp'
  | 'medworldInstitute'
  | 'lockup';

export type MilestoneItem = {
  title: string;
  description?: string;
  tile: MilestoneTile;
  image?: { src: string; alt: string; width: number; height: number };
  // External links open in a new tab
  readMoreUrl?: string;
};

export type Milestone = {
  year: string;
  compact?: boolean;
  items: MilestoneItem[];
};

export const MILESTONES: Milestone[] = [
  {
    year: '2006',
    compact: true,
    items: [{ title: 'Founded Medrecruit', tile: 'medrecruit' }],
  },
  {
    year: '2012',
    compact: true,
    items: [
      {
        title: 'Research into burnout',
        tile: 'photo',
        image: {
          src: `${ASSETS}/milestone-burnout-research.jpg`,
          alt: 'Doctor presenting research in a lecture theatre',
          width: 1570,
          height: 1556,
        },
        readMoreUrl:
          'https://www.stuff.co.nz/national/health/97768696/kiwi-doctor-leads-shift-to-address-burnout-for-fellow-doctors',
      },
      {
        title: 'Ernst & Young, Young Entrepreneur Of The Year',
        tile: 'photo',
        image: {
          src: `${ASSETS}/milestone-ey-award.jpg`,
          alt: 'Ernst & Young Entrepreneur Of The Year awards on stage',
          width: 1600,
          height: 1109,
        },
        readMoreUrl:
          'https://www.medenterprises.com/news/medrecruit-founder-dr-sam-hazledine-awarded-prestigious-entrepreneur-of-the-year-award',
      },
    ],
  },
  {
    year: '2014',
    items: [
      {
        title: 'Four years on the Deloitte Fast 50',
        description:
          "We spent four consecutive years on the Deloitte Fast 50, recognition of our growth as one of the region's fastest-growing companies.",
        tile: 'deloitte',
      },
    ],
  },
  {
    year: '2017',
    items: [
      {
        title: 'Lobbying to change the modern Hippocratic oath',
        description:
          "Our founder Dr Sam Hazledine successfully lobbied the World Medical Association to add doctors' own health and wellbeing to the Declaration of Geneva, the modern Hippocratic oath.",
        tile: 'photoFlipped',
        image: {
          src: `${ASSETS}/milestone-declaration-of-geneva.jpg`,
          alt: 'Dr Sam Hazledine speaking on stage',
          width: 1000,
          height: 613,
        },
        readMoreUrl: 'https://www.medenterprises.com/news/declaration-of-geneva',
      },
    ],
  },
  {
    year: '2020',
    items: [
      {
        title: 'Laying the foundations for Medworld',
        description:
          'We began building a digital platform designed to break down silos between doctors and enable real collaboration across the profession.',
        tile: 'medworld',
      },
    ],
  },
  {
    year: '2023',
    items: [
      {
        title: 'Earning B Corp certification',
        description:
          "We earned B Corp certification, external recognition of our commitment to enriching doctors' lives and providing medical staffing where it's needed most.",
        tile: 'bcorp',
        readMoreUrl: PATH.B_CORP,
      },
    ],
  },
  {
    year: '2024',
    items: [
      {
        title: 'Launch of Medworld Institute',
        description:
          'We launch the Medworld Institute to support the new generation of doctors, through scholarship, education and research.',
        tile: 'medworldInstitute',
        readMoreUrl: 'https://institute.medworld.com/',
      },
    ],
  },
  {
    year: '2026',
    items: [
      {
        title: '20 years, and just getting started',
        description:
          'This year we celebrate two decades of placing doctors and supporting the employers who rely on them, the biggest chapter yet in the Medrecruit story.',
        tile: 'lockup',
      },
    ],
  },
];
